// frontend/src/features/portfolio/lib/lastAccessedPortfolio.js
/**
 * The client a PM most recently opened on the portfolio detail page
 * (/client/:id), used to autofill the report portfolio picker so they can press
 * Enter and generate immediately.
 *
 * Session-scoped on purpose (the autofill must NOT outlive the login):
 *  - kept in module memory (survives SPA navigation between the detail page and
 *    a report popup), and
 *  - mirrored to sessionStorage so it also survives an in-tab reload, but never
 *    to localStorage - the last-viewed client must not linger on disk or bleed
 *    into the next user's session on a shared machine.
 * Cleared on logout via AuthContext.clearTokens (alongside the other caches).
 *
 * Stores only { codename, name, clientId } - the codename is what the report
 * selector resolves against; the name is for display and is already present in
 * the firm-scoped /codenames payload (no extra PII surface). See
 * docs/superpowers/specs/2026-06-29-report-shortname-and-autofill-design.md.
 */
import { STORAGE_KEYS } from '../../../config/constants';

function readFromSession() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEYS.LAST_ACCESSED_PORTFOLIO);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && parsed.codename ? parsed : null;
  } catch (_) {
    return null;
  }
}

let current = readFromSession();

/**
 * Record the last-accessed client. A no-op without a codename (the selector
 * cannot resolve an entry that has none).
 * @param {{codename: string, name?: string, clientId?: number|string|null}} entry
 */
export function setLastAccessedPortfolio(entry) {
  if (!entry || !entry.codename) return;
  current = {
    codename: entry.codename,
    name: entry.name || '',
    clientId: entry.clientId ?? null,
  };
  try {
    sessionStorage.setItem(
      STORAGE_KEYS.LAST_ACCESSED_PORTFOLIO,
      JSON.stringify(current),
    );
  } catch (_) {
    // sessionStorage full/unavailable - module memory still serves this session.
  }
}

/** The last-accessed client, or null. Falls back to sessionStorage after a reload. */
export function getLastAccessedPortfolio() {
  return current || readFromSession();
}

/** Drop the last-accessed pointer (call on logout / token clear). */
export function clearLastAccessedPortfolio() {
  current = null;
  try {
    sessionStorage.removeItem(STORAGE_KEYS.LAST_ACCESSED_PORTFOLIO);
  } catch (_) {
    // sessionStorage unavailable - nothing to clear.
  }
}
